/**
 * The in-game clock: a day is a fixed span of real time counted from one epoch, so every map server, page and process
 * that reads the wall clock agrees on the day without talking to another. Pure.
 */

import clock from "./clock.json";

/**
 * The instant day 0 begins, in milliseconds since the Unix epoch (`clock.json`: 2026-10-01 00:00 UTC).
 */
export const GAME_EPOCH_MS = Date.parse(clock.epoch);

/**
 * The length of one in-game day in milliseconds: four real hours.
 */
export const GAME_DAY_MS = clock.dayHours * 60 * 60 * 1000;

/**
 * The in-game day a wall-clock time falls in.
 *
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The whole days since `GAME_EPOCH_MS`; 0 for a time before it or one that is not finite.
 * @example
 * gameDayOf(GAME_EPOCH_MS + GAME_DAY_MS);
 * // 1
 */
export function gameDayOf(epochMs: number): number {
  return Number.isFinite(epochMs) && epochMs > GAME_EPOCH_MS ? Math.floor((epochMs - GAME_EPOCH_MS) / GAME_DAY_MS) : 0;
}

/**
 * The length of one turn in milliseconds: a quarter of a game day, one real hour.
 */
export const TURN_MS = 60 * 60 * 1000;

/**
 * The turns in one game day.
 */
export const TURNS_PER_DAY = 4;

/**
 * The game hours in one turn.
 */
export const HOURS_PER_TURN = 6;

/**
 * The length of one game hour in milliseconds: ten real minutes, the step crops grow by.
 */
export const GAME_HOUR_MS = TURN_MS / HOURS_PER_TURN;

/**
 * The days in one season: three months of 33 days (Bible 2.5).
 */
export const SEASON_DAYS = 99;

/**
 * The seasons of the year in order, from the day the clock starts: Greening, Sunheight, Aftermath, Deeptime.
 */
export const SEASONS = ["greening", "sunheight", "aftermath", "deeptime"] as const;

/**
 * One season of the year.
 */
export type Season = (typeof SEASONS)[number];

/**
 * The days in one year: four seasons.
 */
export const YEAR_DAYS = SEASON_DAYS * SEASONS.length;

/**
 * The turn a wall-clock time falls in.
 *
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The whole turns since `GAME_EPOCH_MS`; 0 for a time before it or one that is not finite.
 * @example
 * turnOf(GAME_EPOCH_MS + 2 * TURN_MS);
 * // 2
 */
export function turnOf(epochMs: number): number {
  return Number.isFinite(epochMs) && epochMs > GAME_EPOCH_MS ? Math.floor((epochMs - GAME_EPOCH_MS) / TURN_MS) : 0;
}

/**
 * The season a game day falls in. Day 0 is the first day of Greening and the year repeats every `YEAR_DAYS`.
 *
 * @param day A whole game day; below 0 or not finite reads as day 0.
 * @returns The season.
 */
export function seasonOfDay(day: number): Season {
  const whole = Number.isFinite(day) && day > 0 ? Math.floor(day) : 0;
  return SEASONS[Math.floor((whole % YEAR_DAYS) / SEASON_DAYS)] as Season;
}

/**
 * The season a turn falls in.
 *
 * @param turn A whole turn; below 0 or not finite reads as turn 0.
 * @returns The season.
 */
export function seasonOfTurn(turn: number): Season {
  return seasonOfDay(Math.floor(turn / TURNS_PER_DAY));
}

/**
 * The season a wall-clock time falls in.
 *
 * @param epochMs The time in milliseconds since the Unix epoch.
 * @returns The season; Greening for a time before the epoch or one that is not finite.
 */
export function seasonOf(epochMs: number): Season {
  return seasonOfDay(gameDayOf(epochMs));
}
